import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import styles from '../Settings/Settings.module.css';

/**
 * /settings/notifications — the destination every product email links to for
 * "Manage preferences" / "Unsubscribe". Transactional emails (verification,
 * password reset) cannot be turned off and are listed as such.
 */
const EMAIL_PREFS = [
    { key: 'product_email', label: 'Product & lifecycle emails', sub: 'First-coin tips, Legendary windows, collection nudges and re-engagement' },
    { key: 'weekly_email',  label: 'Digest emails', sub: 'A daily summary of your coins and progress, only when there is something to report' },
];
const PUSH_PREFS = [
    { key: 'legendary_push',      label: 'Legendary attempts',   sub: 'When a Legendary window opens' },
    { key: 'coin_cooldowns_push', label: 'Coin upgrades',        sub: 'When a coin is ready to upgrade, plus your weekly summary and comeback reminders' },
    { key: 'collections_push',    label: 'Collections',          sub: 'Progress and completion nudges' },
    { key: 'watchlist_push',      label: 'Watchlist',            sub: 'When a saved title is ready to verify' },
    { key: 'social_push',         label: 'Social',               sub: 'Followers and reactions' },
    { key: 'friend_activity_push',label: 'Friend activity',      sub: 'Legendary, Series Mastery and Collection coins from people you follow' },
];

export default function NotificationSettings() {
    const navigate = useNavigate();
    const [prefs, setPrefs] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        apiRequest('/notifications/preferences')
            .then((data) => setPrefs(data))
            .catch((err) => setError(err.message || 'Could not load your preferences.'));
    }, []);

    const toggle = async (key) => {
        if (!prefs || saving) return;
        const next = !prefs[key];
        setSaving(key);
        setError(null);
        try {
            const updated = await apiRequest('/notifications/preferences', { method: 'PATCH', body: { [key]: next } });
            setPrefs(updated?.[key] !== undefined ? updated : { ...prefs, [key]: next });
        } catch (err) {
            setError(err.message || 'Could not save that change.');
        } finally {
            setSaving(null);
        }
    };

    const unsubscribeAll = async () => {
        if (saving) return;
        setSaving('all');
        try {
            const updated = await apiRequest('/notifications/preferences', { method: 'PATCH', body: { product_email: false, weekly_email: false } });
            setPrefs(updated || { ...prefs, product_email: false, weekly_email: false });
        } catch (err) {
            setError(err.message || 'Could not save that change.');
        } finally {
            setSaving(null);
        }
    };

    const renderRow = ({ key, label, sub }) => (
        <div className={styles.settingsRow} key={key}>
            <div>
                <div className={styles.rowLabel}>{label}</div>
                <div className={styles.rowSub}>{sub}</div>
            </div>
            <label className={styles.switch}>
                <input type="checkbox" checked={!!prefs?.[key]} onChange={() => toggle(key)} disabled={!prefs || !!saving} aria-label={label} />
                <span className={styles.slider} />
            </label>
        </div>
    );

    return (
        <main className={styles.page}>
            <div className={styles.inner}>
                <div className={styles.screenHeader}>
                    <button className={styles.backBtn} onClick={() => navigate('/settings')} title="Back to settings">← Back</button>
                    <h1 className={styles.screenTitle}>Email & Notifications</h1>
                    <div style={{ width: '60px' }} />
                </div>

                {error && <p style={{ color: '#EF4444', margin: 0 }}>{error}</p>}

                <section className={styles.section}>
                    <h2 className={styles.sectionLabel}>Email</h2>
                    <div className={styles.settingsGroup}>
                        {EMAIL_PREFS.map((p, i) => (
                            <div key={p.key}>
                                {i > 0 && <div className={styles.divider} />}
                                {renderRow(p)}
                            </div>
                        ))}
                        <div className={styles.divider} />
                        <div className={styles.settingsRow}>
                            <div>
                                <div className={styles.rowLabel}>Account emails</div>
                                <div className={styles.rowSub}>Email verification, password resets and security notices are always sent.</div>
                            </div>
                        </div>
                    </div>
                    <button className={styles.backBtn} style={{ alignSelf: 'flex-start', marginTop: 8 }} onClick={unsubscribeAll} disabled={!prefs || !!saving}>
                        Unsubscribe from all optional emails
                    </button>
                </section>

                <section className={styles.section}>
                    <h2 className={styles.sectionLabel}>Push notifications</h2>
                    <div className={styles.settingsGroup}>
                        {PUSH_PREFS.map((p, i) => (
                            <div key={p.key}>
                                {i > 0 && <div className={styles.divider} />}
                                {renderRow(p)}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </main>
    );
}
